import { Box, FormText, PrimaryButton, SecondaryButton, FeedbackMessage, Grid } from "@husqvarna/ui-core";
import { useState } from "react";
import { useIntl } from "react-intl";
import { useForm } from "react-hook-form";

import { useSaveAccountLevelMutation } from "../../../projectApi";
import AccountLevel from '../../../models/AccountLevel';
import UpdateAccountLevelModel from "../../../models/UpdateAccountLevelModel";
import { pushToDataLayer } from "../../../../shared/utils/tracking";

type AccountLevelFormProps = {
  accountLevel?: AccountLevel;
  onFormSubmitted?: (accountLevel: AccountLevel | undefined) => void;
  onCancel?: () => void;
}

const createDefaultValues = (accountLevel?: AccountLevel) => {
  if (!accountLevel) {
    return {};
  }

  const accountLevelFormModel: UpdateAccountLevelModel = {
    id: accountLevel.id,
    name: accountLevel.name
  }

  return accountLevelFormModel;
}

const AccountLevelForm = ({
  accountLevel,
  onFormSubmitted,
  onCancel
}: AccountLevelFormProps) => {
  const intl = useIntl();
  const { register, handleSubmit, formState: { errors, isDirty } } = useForm<UpdateAccountLevelModel>({
    defaultValues: createDefaultValues(accountLevel)
  });

  const [saveAccountLevel] = useSaveAccountLevelMutation();
  const [submitFailed, setSubmitFailed] = useState(false);
  const isNewAccountLevel = accountLevel === undefined;

  const onSubmit = (model: UpdateAccountLevelModel) => {
    setSubmitFailed(false);
    const submitFn = saveAccountLevel;
    submitFn.call(this, model).then(res => {
      if ("error" in res) {
        setSubmitFailed(true);
      }
      else {
        let accountLevel: AccountLevel | undefined;
        if ("data" in res) {
            accountLevel = res.data;
        }
        trackAccountLevel(model);
        onFormSubmitted?.(accountLevel);
      }
    });
  }

  const trackAccountLevel = (model: UpdateAccountLevelModel) => {
    const eventName = isNewAccountLevel ? "createAccountLevel" : "updateAccountLevel";
    pushToDataLayer({
      event: eventName,
      accountLevel: {
        accountLevelId: isNewAccountLevel ? 'undefined' : `${model.id}`,
        accountLevelName: `${model.name}`,
      }
    });
  }

  const getSaveButtonText = () => {
    console.log('isNewAccountLevel', isNewAccountLevel);
    return isNewAccountLevel ? intl.formatMessage({ id: "accountLevel.editForm.create", defaultMessage: "Create folder" }) : intl.formatMessage({ id: "accountLevel.editForm.save", defaultMessage: "Update folder" });
  }

  return (
    <form onSubmit={handleSubmit(onSubmit)}>
      <FormText
        label={intl.formatMessage({ id: "accountLevel.editForm.Name", defaultMessage: "Folder name" })}
        {...register("name", {
          required: intl.formatMessage({
            id: "forms.editAccountLevelInfo.fieldRequired",
            defaultMessage: "This field is required",
            description: "Form error message for required field",
          }),
        })}
        error={errors.name?.message}></FormText>

      {submitFailed && (
        <Box mt={2}>
            <FeedbackMessage variant="ErrorRed"
            message={intl.formatMessage({
                id: "accountLevel.editForm.submitFailedMessage",
                defaultMessage: "Folder could not be saved. Please try again."
            })}></FeedbackMessage>
        </Box>
      )}
      <Box mt={7} flexDirection="row" alignItems="center" justifyContent="flex-end">
        <SecondaryButton text={intl.formatMessage({ id: "general.cancel", defaultMessage: "Cancel" })} onClick={onCancel} />
        <PrimaryButton ml={4} disabled={!isDirty} submit text={getSaveButtonText()} />
      </Box>
    </form>
  );
}

export default AccountLevelForm;
